(() => {
  if (customElements.get('product-purchase-options')) return;

  class ProductPurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.productInfo = this.closest('product-info');
      this.list = this.querySelector('[data-purchase-options-list]');
      this.cardTemplate = this.querySelector('[data-purchase-option-template]');
      this.enhancedTemplate = this.querySelector('[data-purchase-enhanced-template]');
      this.variantInput = this.querySelector('[data-purchase-options-variant]');
      this.sellingPlanInput = this.querySelector('[data-purchase-options-selling-plan]');
      this.quantityInput = this.querySelector('[data-purchase-options-quantity]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton?.querySelector('span');
      this.status = this.querySelector('[data-purchase-options-status]');

      this.defaultSelection = this.dataset.defaultSelection || 'first-subscription';
      this.showOneTime = this.dataset.showOnetime === 'true';
      this.oneTimePosition = this.dataset.onetimePosition || 'last';
      this.featureFirstPlan = this.dataset.featureFirstPlan === 'true';
      this.showEnhancedContent = this.dataset.showEnhancedContent === 'true';
      this.featuredLabel = this.dataset.featuredLabel || '';
      this.planFallbackText = this.dataset.planFallbackText || '';
      this.oneTimeTitle = this.dataset.onetimeTitle || 'One-Time Purchase';
      this.oneTimeDescription = this.dataset.onetimeDescription || '';
      this.requiresSellingPlan = this.dataset.requiresSellingPlan === 'true';
      this.showDiscount = this.dataset.showDiscount === 'true';
      this.showComparePrice = this.dataset.showComparePrice === 'true';
      this.moneyFormat = this.parseMoneyFormat(this.dataset.moneyFormat);

      this.currencyCode =
        this.dataset.currencyCode ||
        window.Shopify?.currency?.active ||
        'USD';

      this.data = this.parseData();
      this.planCopy = this.parsePlanCopy();

      this.variants = new Map(
        (this.data.variants || []).map((variant) => [
          String(variant.id),
          variant,
        ])
      );

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onExternalChange = this.onExternalChange.bind(this);
      this.onKnownVariantEvent = this.onKnownVariantEvent.bind(this);

      this.addEventListener(
        'change',
        this.onOptionChange
      );

      this.productInfo?.addEventListener(
        'change',
        this.onExternalChange,
        true
      );

      document.addEventListener(
        'variant:change',
        this.onKnownVariantEvent
      );

      document.addEventListener(
        'product:variant-change',
        this.onKnownVariantEvent
      );

      if (
        typeof window.subscribe === 'function' &&
        window.PUB_SUB_EVENTS?.variantChange
      ) {
        this.unsubscribeVariant = window.subscribe(
          window.PUB_SUB_EVENTS.variantChange,
          (event) => {
            const variant =
              event?.data?.variant ||
              event?.variant;

            if (variant?.id) {
              this.setVariant(
                String(variant.id)
              );
            }
          }
        );
      }

      this.setVariant(
        this.findCurrentVariantId()
      );
    }

    disconnectedCallback() {
      this.removeEventListener(
        'change',
        this.onOptionChange
      );

      this.productInfo?.removeEventListener(
        'change',
        this.onExternalChange,
        true
      );

      document.removeEventListener(
        'variant:change',
        this.onKnownVariantEvent
      );

      document.removeEventListener(
        'product:variant-change',
        this.onKnownVariantEvent
      );

      if (
        typeof this.unsubscribeVariant === 'function'
      ) {
        this.unsubscribeVariant();
      }
    }

    parseData() {
      const node = this.querySelector(
        '[data-purchase-options-data]'
      );

      if (!node) {
        return {
          variants: [],
        };
      }

      try {
        return JSON.parse(
          node.textContent
        );
      } catch (error) {
        console.error(
          'Unable to parse purchase option data.',
          error
        );

        return {
          variants: [],
        };
      }
    }

    parsePlanCopy() {
      const node = this.querySelector(
        '[data-purchase-options-copy]'
      );

      if (!node) return {};

      try {
        const parsed = JSON.parse(
          node.textContent
        );

        return (
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed)
        )
          ? parsed
          : {};
      } catch (error) {
        console.error(
          'Unable to parse purchase plan descriptions.',
          error
        );

        return {};
      }
    }

    parseMoneyFormat(value) {
      if (!value) {
        return '${{amount}}';
      }

      try {
        return JSON.parse(value);
      } catch (_error) {
        return value;
      }
    }

    normalize(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[–—-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    findCurrentVariantId() {
      const urlVariant =
        new URL(window.location.href)
          .searchParams
          .get('variant');

      if (
        urlVariant &&
        this.variants.has(String(urlVariant))
      ) {
        return String(urlVariant);
      }

      const externalInput =
        this.productInfo?.querySelector(
          'form[id^="product-form-installment"] input[name="id"], input.product-variant-id:not([data-purchase-options-variant])'
        );

      if (
        externalInput?.value &&
        this.variants.has(
          String(externalInput.value)
        )
      ) {
        return String(
          externalInput.value
        );
      }

      return String(
        this.variantInput?.value ||
        this.data.variants?.[0]?.id ||
        ''
      );
    }

    onExternalChange(event) {
      if (this.contains(event.target)) {
        return;
      }

      [0, 60, 180].forEach((delay) => {
        window.setTimeout(
          () => {
            this.setVariant(
              this.findCurrentVariantId()
            );
          },
          delay
        );
      });
    }

    onKnownVariantEvent(event) {
      const variant =
        event?.detail?.variant ||
        event?.detail?.data?.variant;

      if (variant?.id) {
        this.setVariant(
          String(variant.id)
        );

        return;
      }

      window.setTimeout(
        () => {
          this.setVariant(
            this.findCurrentVariantId()
          );
        },
        0
      );
    }

    setVariant(variantId) {
      const variant =
        this.variants.get(
          String(variantId)
        );

      if (!variant) return;

      const previousSelection =
        this.readSelection();

      /*
       * This is the variant Shopify/the theme currently considers selected.
       * It is also the variant used by the one-time purchase card.
       *
       * Subscription cards are allowed to target OTHER product variants.
       * That is what lets a 1-bottle monthly plan and a 3-bottle 3-month
       * plan appear together in the same custom purchase-options UI.
       */
      this.baseVariant = variant;
      this.currentVariant = variant;

      this.renderOptions(
        variant
      );

      const desiredKey =
        this.resolveSelection(
          previousSelection,
          variant
        );

      if (desiredKey) {
        this.selectOption(
          desiredKey
        );

        this.clearStatus();
      } else {
        this.clearFormSelection();

        this.disableForm(
          'No purchase option is available for this product.'
        );
      }

      this.classList.add(
        'is-ready'
      );
    }

    renderOptions(baseVariant) {
      if (
        !this.list ||
        !this.cardTemplate
      ) {
        return;
      }

      this.list.replaceChildren();

      /*
       * IMPORTANT:
       *
       * Selling plans are collected from EVERY product variant, not only
       * the currently selected variant. Each rendered plan remembers the
       * variant that owns that selling-plan allocation.
       *
       * Example:
       * - Monthly Delivery -> 1 Bottle variant -> quantity 1
       * - 3 Month Supply   -> 3 Bottle variant -> quantity 1
       */
      const planOptions =
        this.collectPlanOptions(
          baseVariant
        );

      const planCards =
        planOptions.map(
          ({ plan, variant }, index) =>
            this.createPlanCard(
              plan,
              variant,
              index
            )
        );

      const canShowOneTime =
        this.showOneTime &&
        !this.requiresSellingPlan;

      const oneTimeCard =
        canShowOneTime
          ? this.createOneTimeCard(
              baseVariant
            )
          : null;

      if (
        oneTimeCard &&
        this.oneTimePosition === 'first'
      ) {
        this.list.append(
          oneTimeCard,
          ...planCards
        );
      } else {
        this.list.append(
          ...planCards
        );

        if (oneTimeCard) {
          this.list.append(
            oneTimeCard
          );
        }
      }

      this.cards = Array.from(
        this.querySelectorAll(
          '[data-purchase-option-card]'
        )
      );

      this.radios = Array.from(
        this.querySelectorAll(
          '[data-purchase-option-input]'
        )
      );
    }

    collectPlanOptions(preferredVariant) {
      const orderedVariants = [];

      const preferredId =
        String(
          preferredVariant?.id || ''
        );

      if (preferredId) {
        const preferred =
          this.variants.get(
            preferredId
          );

        if (preferred) {
          orderedVariants.push(
            preferred
          );
        }
      }

      (this.data.variants || []).forEach(
        (variant) => {
          if (
            String(variant.id) ===
            preferredId
          ) {
            return;
          }

          orderedVariants.push(
            variant
          );
        }
      );

      const seenPlanIds =
        new Set();

      const planOptions = [];

      orderedVariants.forEach(
        (variant) => {
          (variant.sellingPlans || []).forEach(
            (plan) => {
              const planId =
                String(
                  plan.id || ''
                );

              if (
                !planId ||
                seenPlanIds.has(planId)
              ) {
                return;
              }

              seenPlanIds.add(
                planId
              );

              planOptions.push({
                plan,
                variant,
              });
            }
          );
        }
      );

      /*
      * Display longer supply plans first.
      *
      * Example:
      * 3 Month Supply
      * Monthly Delivery
      */
      planOptions.sort(
        (a, b) => {
          const getPriority =
            (item) => {
              const plan =
                item.plan || {};

              const values = [
                plan.name,
                plan.description,
                ...(plan.options || []).flatMap(
                  (option) => [
                    option.name,
                    option.value,
                  ]
                ),
              ]
                .map((value) =>
                  this.normalize(value)
                )
                .filter(Boolean)
                .join(' ');

              if (
                /\b3\s+month\b/.test(values) ||
                /\b3\s+months\b/.test(values) ||
                /\bthree\s+month\b/.test(values)
              ) {
                return 0;
              }

              if (
                /\bmonthly\b/.test(values) ||
                /\b1\s+month\b/.test(values) ||
                /\bevery\s+month\b/.test(values)
              ) {
                return 1;
              }

              return 2;
            };

          return (
            getPriority(a) -
            getPriority(b)
          );
        }
      );

      return planOptions;
    }

    createPlanCard(
      plan,
      variant,
      index
    ) {
      const card =
        this.cloneCard();

      const key =
        `plan:${plan.id}`;

      const description =
        this.planDescription(plan);

      this.configureCard(
        card,
        {
          key,
          kind: 'plan',
          variantId:
            String(variant.id),
          planId:
            String(plan.id),
          planName:
            plan.name || '',
          title:
            plan.name ||
            'Subscription',
          description,
          price:
            Number(
              plan.price || 0
            ),
          comparePrice:
            Number(
              plan.compareAtPrice ||
              variant.price ||
              0
            ),
          featured:
            this.featureFirstPlan &&
            index === 0,
          quantity: 1,
        }
      );

      if (
        this.showEnhancedContent &&
        this.enhancedTemplate
      ) {
        const enhanced =
          this.enhancedTemplate
            .content
            .firstElementChild
            ?.cloneNode(true);

        if (enhanced) {
          enhanced.hidden = true;

          card.appendChild(
            enhanced
          );
        }
      }

      return card;
    }

    createOneTimeCard(variant) {
      const card =
        this.cloneCard();

      this.configureCard(
        card,
        {
          key: 'one-time',
          kind: 'one-time',
          variantId:
            String(variant.id),
          planId: '',
          planName: '',
          title:
            this.oneTimeTitle,
          description:
            this.oneTimeDescription,
          price:
            Number(
              variant.price || 0
            ),
          comparePrice:
            Number(
              variant.compareAtPrice ||
              0
            ),
          featured: false,
          quantity: 1,
        }
      );

      return card;
    }

    cloneCard() {
      return this.cardTemplate
        .content
        .firstElementChild
        .cloneNode(true);
    }

    configureCard(
      card,
      option
    ) {
      const radio =
        card.querySelector(
          '[data-purchase-option-input]'
        );

      const ribbon =
        card.querySelector(
          '[data-purchase-ribbon]'
        );

      const title =
        card.querySelector(
          '[data-purchase-title]'
        );

      const description =
        card.querySelector(
          '[data-purchase-description]'
        );

      const descriptionCopy =
        card.querySelector(
          '[data-purchase-description-copy]'
        );

      const descriptionSeparator =
        card.querySelector(
          '[data-purchase-description-separator]'
        );

      const descriptionSaving =
        card.querySelector(
          '[data-purchase-description-saving]'
        );

      const price =
        card.querySelector(
          '[data-purchase-price]'
        );

      const compare =
        card.querySelector(
          '[data-purchase-compare]'
        );

      const discount =
        card.querySelector(
          '[data-purchase-discount]'
        );

      const quantity =
        Number(
          option.quantity || 1
        );

      card.dataset.purchaseOptionCard =
        option.key;

      card.dataset.optionKind =
        option.kind;

      card.dataset.variantId =
        option.variantId;

      card.dataset.planId =
        option.planId;

      card.dataset.planName =
        option.planName;

      card.dataset.quantity =
        String(quantity);

      if (quantity > 1) {
        card.classList.add(
          'product-purchase-option--multi-unit'
        );
      }

      if (radio) {
        radio.value =
          option.key;

        radio.dataset.optionKind =
          option.kind;

        radio.dataset.variantId =
          option.variantId;

        radio.dataset.planId =
          option.planId;

        radio.dataset.planName =
          option.planName;

        radio.dataset.quantity =
          String(quantity);
      }

      if (title) {
        title.textContent =
          option.title;
      }

      const discountPercent =
        option.comparePrice >
        option.price
          ? Math.round(
              (
                1 -
                option.price /
                  option.comparePrice
              ) *
                100
            )
          : 0;

      const showDiscount =
        this.showDiscount &&
        discountPercent > 0;

      const showDescriptionSaving =
        option.kind === 'plan' &&
        discountPercent > 0;

      if (descriptionCopy) {
        descriptionCopy.textContent =
          option.description || '';
      }

      if (descriptionSaving) {
        descriptionSaving.textContent =
          showDescriptionSaving
            ? `Save ${discountPercent}%`
            : '';

        descriptionSaving.hidden =
          !showDescriptionSaving;
      }

      if (descriptionSeparator) {
        descriptionSeparator.hidden =
          !(
            option.description &&
            showDescriptionSaving
          );
      }

      if (description) {
        description.hidden =
          !(
            option.description ||
            showDescriptionSaving
          );
      }

      /*
       * Each purchase option now points at the Shopify variant that already
       * represents the correct pack size. Therefore the allocation price is
       * already the full price for that option and must NOT be multiplied.
       */
      const displayPrice =
        option.price;

      const displayComparePrice =
        option.comparePrice;

      if (price) {
        price.textContent =
          this.formatMoney(
            displayPrice
          );
      }

      const showCompare =
        this.showComparePrice &&
        displayComparePrice >
          displayPrice;

      if (compare) {
        compare.hidden =
          !showCompare;

        compare.textContent =
          showCompare
            ? this.formatMoney(
                displayComparePrice
              )
            : '';
      }

      if (discount) {
        discount.hidden =
          !showDiscount;

        discount.textContent =
          showDiscount
            ? `${discountPercent}% Off`
            : '';
      }

      if (
        option.featured &&
        this.featuredLabel
      ) {
        card.classList.add(
          'product-purchase-option--featured'
        );

        if (ribbon) {
          ribbon.hidden = false;

          ribbon.textContent =
            this.featuredLabel;
        }
      } else if (ribbon) {
        ribbon.hidden = true;
      }

      return card;
    }

    planDescription(plan) {
      const override =
        this.planDescriptionOverride(
          plan
        );

      if (override) {
        return override;
      }

      const description =
        String(
          plan.description || ''
        ).trim();

      if (description) {
        return description;
      }

      const cadence =
        this.cadenceDescription(
          plan
        );

      if (cadence) {
        return cadence;
      }

      const planName =
        this.normalize(
          plan.name
        );

      const optionValues =
        (plan.options || [])
          .map(
            (option) =>
              String(
                option.value || ''
              ).trim()
          )
          .filter(Boolean)
          .filter(
            (value) =>
              this.normalize(
                value
              ) !== planName
          );

      if (optionValues.length) {
        return [
          ...new Set(
            optionValues
          ),
        ].join(' · ');
      }

      return this.planFallbackText;
    }

    planDescriptionOverride(plan) {
      const byId =
        this.planCopy[
          String(plan.id)
        ];

      if (byId) {
        return this.planCopyValue(
          byId
        );
      }

      const byExactName =
        this.planCopy[
          String(
            plan.name || ''
          )
        ];

      if (byExactName) {
        return this.planCopyValue(
          byExactName
        );
      }

      const normalizedName =
        this.normalize(
          plan.name
        );

      const matchingEntry =
        Object.entries(
          this.planCopy
        ).find(
          ([key]) =>
            this.normalize(
              key
            ) === normalizedName
        );

      return matchingEntry
        ? this.planCopyValue(
            matchingEntry[1]
          )
        : '';
    }

    planCopyValue(value) {
      if (
        typeof value === 'string'
      ) {
        return value.trim();
      }

      if (
        value &&
        typeof value === 'object'
      ) {
        return String(
          value.description ||
          value.text ||
          value.subtitle ||
          ''
        ).trim();
      }

      return '';
    }

    cadenceDescription(plan) {
      const values = [
        ...(plan.options || []).map(
          (option) =>
            option.value
        ),
        plan.name,
      ]
        .map(
          (value) =>
            String(
              value || ''
            ).trim()
        )
        .filter(Boolean);

      for (
        const value of values
      ) {
        const normalized =
          this.normalize(
            value
          );

        let match =
          normalized.match(
            /^every\s+(\d+)\s+days?$/
          );

        if (match) {
          return `Delivered every ${match[1]} day${match[1] === '1' ? '' : 's'}`;
        }

        match =
          normalized.match(
            /^(\d+)\s+days?/
          );

        if (match) {
          return `Delivered every ${match[1]} day${match[1] === '1' ? '' : 's'}`;
        }

        match =
          normalized.match(
            /^(?:every\s+)?(\d+)\s+months?/
          );

        if (match) {
          return `Delivered every ${match[1]} month${match[1] === '1' ? '' : 's'}`;
        }

        match =
          normalized.match(
            /^(?:every\s+)?(\d+)\s+weeks?/
          );

        if (match) {
          return `Delivered every ${match[1]} week${match[1] === '1' ? '' : 's'}`;
        }

        if (
          /\bmonthly\b/.test(
            normalized
          )
        ) {
          return 'Delivered every month';
        }

        if (
          /\bweekly\b/.test(
            normalized
          )
        ) {
          return 'Delivered every week';
        }

        if (
          /\bbiweekly\b|\bfortnightly\b/.test(
            normalized
          )
        ) {
          return 'Delivered every 2 weeks';
        }
      }

      return '';
    }

    readSelection() {
      const checked =
        this.querySelector(
          '[data-purchase-option-input]:checked'
        );

      if (!checked) {
        return null;
      }

      return {
        key:
          checked.value,

        kind:
          checked.dataset.optionKind ||
          '',

        variantId:
          checked.dataset.variantId ||
          '',

        planId:
          checked.dataset.planId ||
          '',

        planName:
          checked.dataset.planName ||
          '',
      };
    }

    resolveSelection(
      previous,
      variant
    ) {
      const radios =
        this.radios || [];

      if (previous?.key) {
        const exact =
          radios.find(
            (radio) =>
              radio.value ===
              previous.key
          );

        if (exact) {
          return exact.value;
        }
      }

      if (
        previous?.kind === 'plan' &&
        previous.planName
      ) {
        const normalizedName =
          this.normalize(
            previous.planName
          );

        const sameName =
          radios.find(
            (radio) =>
              radio.dataset.optionKind ===
                'plan' &&
              this.normalize(
                radio.dataset.planName
              ) ===
                normalizedName
          );

        if (sameName) {
          return sameName.value;
        }
      }

      const firstPlan =
        radios.find(
          (radio) =>
            radio.dataset.optionKind ===
            'plan'
        );

      const oneTime =
        radios.find(
          (radio) =>
            radio.dataset.optionKind ===
            'one-time'
        );

      if (
        this.defaultSelection ===
          'one-time' &&
        oneTime
      ) {
        return oneTime.value;
      }

      if (firstPlan) {
        return firstPlan.value;
      }

      if (oneTime) {
        return oneTime.value;
      }

      return '';
    }

    onOptionChange(event) {
      const radio =
        event.target.closest(
          '[data-purchase-option-input]'
        );

      if (!radio) {
        return;
      }

      this.selectOption(
        radio.value
      );
    }

    selectOption(key) {
      const radio =
        (this.radios || []).find(
          (candidate) =>
            candidate.value ===
            key
        );

      if (!radio) {
        return;
      }

      const variantId =
        String(
          radio.dataset.variantId ||
          this.baseVariant?.id ||
          ''
        );

      const optionVariant =
        this.variants.get(
          variantId
        );

      if (!optionVariant) {
        this.clearFormSelection();

        this.disableForm(
          'This purchase option is unavailable.'
        );

        return;
      }

      this.currentVariant =
        optionVariant;

      radio.checked = true;

      (this.cards || []).forEach(
        (card) => {
          const selected =
            card.dataset
              .purchaseOptionCard ===
            key;

          card.classList.toggle(
            'is-selected',
            selected
          );

          const enhanced =
            card.querySelector(
              '[data-purchase-enhanced]'
            );

          if (enhanced) {
            enhanced.hidden =
              !selected;
          }
        }
      );

      /*
       * The purchase card controls BOTH the variant and selling plan.
       *
       * A 3-bottle subscription therefore submits the 3-bottle variant
       * once, rather than submitting the 1-bottle variant three times.
       */
      if (this.variantInput) {
        this.variantInput.value =
          String(
            optionVariant.id
          );

        this.variantInput.disabled =
          !optionVariant.available;
      }

      const sellingPlanId =
        radio.dataset.optionKind ===
        'plan'
          ? radio.dataset.planId ||
            ''
          : '';

      if (
        this.sellingPlanInput
      ) {
        this.sellingPlanInput.value =
          sellingPlanId;

        this.sellingPlanInput.disabled =
          !sellingPlanId;
      }

      /*
       * Each Shopify variant already represents its correct pack size.
       *
       * 1 Bottle variant = quantity 1
       * 3 Bottle variant = quantity 1
       */
      if (this.quantityInput) {
        this.quantityInput.value =
          '1';
      }

      this.clearStatus();

      this.updateSubmitState();
    }

    clearFormSelection() {
      if (this.baseVariant) {
        this.currentVariant =
          this.baseVariant;

        if (this.variantInput) {
          this.variantInput.value =
            String(
              this.baseVariant.id
            );

          this.variantInput.disabled =
            !this.baseVariant.available;
        }
      }

      if (
        this.sellingPlanInput
      ) {
        this.sellingPlanInput.value =
          '';

        this.sellingPlanInput.disabled =
          true;
      }

      if (
        this.quantityInput
      ) {
        this.quantityInput.value =
          '1';
      }
    }

    updateSubmitState() {
      if (
        !this.submitButton ||
        !this.currentVariant
      ) {
        return;
      }

      const hasSelection =
        Boolean(
          this.querySelector(
            '[data-purchase-option-input]:checked'
          )
        );

      const isAvailable =
        Boolean(
          this.currentVariant.available
        ) &&
        hasSelection;

      this.submitButton.disabled =
        !isAvailable;

      this.submitButton.toggleAttribute(
        'aria-disabled',
        !isAvailable
      );

      if (
        this.submitButtonText
      ) {
        this.submitButtonText.textContent =
          isAvailable
            ? window.variantStrings
                ?.addToCart ||
              'Add to cart'
            : window.variantStrings
                ?.soldOut ||
              'Sold out';
      }
    }

    disableForm(message) {
      if (
        this.submitButton
      ) {
        this.submitButton.disabled =
          true;

        this.submitButton.setAttribute(
          'aria-disabled',
          'true'
        );
      }

      this.setStatus(
        message
      );
    }

    setStatus(message) {
      if (!this.status) {
        return;
      }

      this.status.textContent =
        message;

      this.status.hidden =
        !message;
    }

    clearStatus() {
      this.setStatus('');
    }

    formatMoney(cents) {
      const amount =
        Number(
          cents || 0
        );

      if (
        window.Shopify?.formatMoney
      ) {
        try {
          return window.Shopify.formatMoney(
            amount,
            this.moneyFormat
          );
        } catch (_error) {
          /*
           * Fall through to Intl.
           */
        }
      }

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang ||
            'en',
          {
            style: 'currency',
            currency:
              this.currencyCode,
          }
        ).format(
          amount / 100
        );
      } catch (_error) {
        return `$${(
          amount / 100
        ).toFixed(2)}`;
      }
    }
  }

  customElements.define(
    'product-purchase-options',
    ProductPurchaseOptions
  );
})();